import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    addDatasetToChannel,
    getSlackChannelDataset,
    getSlackChannels,
    getSlackConnection,
    removeDatasetFromChannel,
    getSlackOAuthUrl,
    disconnectSlack,
} from './slackApi'

export const useSlack = (connectionKey, inboxID) => {
    const queryClient = useQueryClient()

    const getTeamId = (connection) => {
        if (connection?.teamID) return connection.teamID
        else if (connection?.additionalParameters)
            return (
                JSON.parse(connection.additionalParameters)?.find((p) => p.field === 'team')?.value
                    ?.id || null
            )
        else return null
    }

    const { data: slackConnection, isLoading: loadingSlackConnection } = useQuery({
        queryKey: ['slackConnection', connectionKey],
        queryFn: () => getSlackConnection(connectionKey),
        enabled: Boolean(connectionKey),
    })

    const { data: slackChannels, isLoading: loadingSlackChannels } = useQuery({
        queryKey: ['slackChannels', connectionKey],
        queryFn: () => getSlackChannels(connectionKey),
        enabled: Boolean(connectionKey),
    })

    const { data: slackChannelDataset, isLoading: loadingSlackChannelDataset } = useQuery({
        queryKey: ['slackChannelDataset', inboxID],
        queryFn: () => getSlackChannelDataset(inboxID),
        enabled: Boolean(inboxID),
    })

    const { mutate: saveSlackSharing, isPending: slackSharingPending } = useMutation({
        mutationFn: ({ slackChannelID }) =>
            addDatasetToChannel(getTeamId(slackConnection), slackChannelID, inboxID),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['slackChannelDataset', inboxID] })
            variables.onSuccess(response)
        },
    })

    const { mutate: deleteSlackSharing, isPending: deleteSharingPending } = useMutation({
        mutationFn: ({ slackChannelID }) => removeDatasetFromChannel(slackChannelID, inboxID),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['slackChannelDataset', inboxID] })
            variables.onSuccess(response)
        },
    })

    const { mutate: getOAuthUrl, isPending: isGettingOAuthUrl } = useMutation({
        mutationFn: () => getSlackOAuthUrl(),
    })

    const { mutate: disconnectSlackConnection, isPending: isDisconnectingSlack } = useMutation({
        mutationFn: ({ connectionKey }) => disconnectSlack(connectionKey),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['slackConnection', variables.connectionKey],
            })
            if (variables.onSuccess) {
                variables.onSuccess(response)
            }
        },
        onError: (error, variables) => {
            if (variables.onError) {
                variables.onError(error)
            }
        },
    })

    return {
        slackConnection,
        loadingSlackConnection,
        slackChannels: slackChannels?.channels || [],
        loadingSlackChannels,
        slackChannelDataset: slackChannelDataset?.objects || [],
        loadingSlackChannelDataset,
        saveSlackSharing,
        slackSharingPending,
        deleteSlackSharing,
        deleteSharingPending,
        getOAuthUrl,
        isGettingOAuthUrl,
        disconnectSlackConnection,
        isDisconnectingSlack,
    }
}
